import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Navigate} from 'react-router-dom';
import {useExistsActiveProductionQuery} from './queries/productionQueries';
import {useMyReportIdsQuery} from './queries/reportQueries';
import {RootState} from './store';
import {ExternalUserRole, InternalUserRole, UserRole} from './types/enums/UserRole';

type anyRole = UserRole | ExternalUserRole | InternalUserRole | undefined;

interface RedirectorProps {
    userRole?: anyRole;
}

// Helper functions for role checking
const isReportViewerRole = (role: anyRole): boolean => {
    return role === UserRole.DIRECTOR ||
        role === UserRole.PARTNER ||
        role === UserRole.EMPTY_USER_MASK ||
        role === UserRole.DL;
};

const isProductionCheckerRole = (role: anyRole): boolean => {
    return role === UserRole.QM_RESPONSIBLE ||
        role === UserRole.SPORTCAST ||
        role === UserRole.ADMIN;
};

const isDirectLiveRole = (role: anyRole): boolean => {
    return role === UserRole.PM_INTERNAL || role === UserRole.PM_EXTERNAL;
};

const Redirector = (props: RedirectorProps) => {
    const reportsFilterAndSort = useSelector((state: RootState) => state.myReports);

    // Only fetch when role requires it
    const shouldFetchReportIds = isReportViewerRole(props.userRole);
    const shouldCheckActiveProduction = isProductionCheckerRole(props.userRole);

    // React Query hooks with conditional enabling
    const {data: reportIds} = useMyReportIdsQuery(reportsFilterAndSort, shouldFetchReportIds);
    const {data: hasActiveProduction} = useExistsActiveProductionQuery(shouldCheckActiveProduction);

    // Derive redirect URL from query results
    const toUrl = useMemo(() => {
        if (!props.userRole) return undefined;

        if (isDirectLiveRole(props.userRole)) {
            return '/live';
        }

        if (shouldFetchReportIds && reportIds !== undefined) {
            if (reportIds.length > 0) {
                return `/reports/${reportIds[0]}`;
            }
            return '/reports';
        }

        if (shouldCheckActiveProduction && hasActiveProduction !== undefined) {
            return hasActiveProduction ? '/live' : '/summary';
        }

        return undefined;
    }, [props.userRole, reportIds, hasActiveProduction, shouldFetchReportIds, shouldCheckActiveProduction]);

    return <>
        {toUrl && <Navigate to={toUrl} replace/>}
    </>;
};

export default Redirector;
